import { ApiError, api, queryKeys } from '@alpha/api-client';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { PageHeader } from '../components/PageHeader';
import { formatNumber, formatUsd } from '../lib/format';
import { Alert, Button, Card, FormGroup, Input, Spinner, StatCard, StatCards } from '../ui';

/**
 * Client's explicit, repeated request: a way to directly enter any member's
 * panel by ID, with no password/credentials, and full access to every
 * sidebar/feature — rather than having to browse Members -> find them ->
 * open their detail page -> click Impersonate. This calls the exact same
 * `impersonate` endpoint MemberDetail.tsx's button does; the only thing new
 * here is the direct-by-ID entry point, placed on the first page an admin
 * lands on.
 */
function EnterMemberPanel() {
  const [memberId, setMemberId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const enter = useMutation({
    mutationFn: (id: string) => api.admin.impersonate(id),
    onSuccess: () => {
      window.location.assign('https://app.alphaone.group/');
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'That member ID could not be found.');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const id = memberId.trim();
    if (!id) return;
    setError(null);
    enter.mutate(id);
  };

  return (
    <Card>
      <h3 className="card-title">Enter Member Panel</h3>
      <p className="text-muted small" style={{ marginBottom: 12 }}>
        Type any member&apos;s ID to go straight into their full user panel — every sidebar item and feature, no
        password needed.
      </p>
      {error ? <Alert tone="danger">{error}</Alert> : null}
      <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: '0 1 220px', minWidth: 180 }}>
          <FormGroup label="Member ID">
            <Input
              placeholder="e.g. AO201865"
              value={memberId}
              onChange={(e) => setMemberId(e.target.value)}
            />
          </FormGroup>
        </div>
        <Button type="submit" variant="gold" disabled={enter.isPending}>
          {enter.isPending ? 'Entering…' : 'Enter Panel'}
        </Button>
      </form>
    </Card>
  );
}

export function Dashboard() {
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.dashboard('admin'),
    queryFn: () => api.admin.dashboard().then((r) => r.data),
  });

  return (
    <>
      <PageHeader title="Dashboard" description="Platform overview" />

      <EnterMemberPanel />

      {isLoading ? (
        <Spinner />
      ) : (
        <>
          <StatCards>
            <StatCard label="Total Members" value={formatNumber(data?.members_total)} />
            <StatCard
              label="Active Members"
              value={formatNumber(data?.members_active)}
              sub={`${formatNumber(data?.members_inactive)} inactive`}
            />
            <StatCard label="Blocked" value={formatNumber(data?.members_blocked)} />
            <StatCard label="Activations Today" value={formatNumber(data?.activations_today)} />
            <StatCard
              label="Deposits Today"
              value={formatUsd(data?.deposits_today_usd)}
              sub={`${formatUsd(data?.deposits_total_usd)} all time`}
            />
            <StatCard
              label="Pending Withdrawals"
              value={formatUsd(data?.withdrawals_pending_usd)}
              sub={`${formatNumber(data?.withdrawals_pending)} requests`}
            />
            <StatCard label="Income Paid" value={formatUsd(data?.income_paid_usd)} />
            <StatCard
              label="Open Tickets"
              value={formatNumber(data?.open_tickets)}
              sub={`${formatNumber(data?.pending_kyc)} KYC pending`}
            />
          </StatCards>

          <Card>
            <h3 className="card-title">Last 30 days</h3>
            <div style={{ width: '100%', height: 300 }}>
              <ResponsiveContainer>
                <LineChart data={data?.chart_daily ?? []}>
                  <CartesianGrid stroke="#1c1c1c" vertical={false} />
                  <XAxis dataKey="date" stroke="#888" fontSize={12} tickLine={false} />
                  <YAxis stroke="#888" fontSize={12} tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={{
                      background: '#161616',
                      border: '1px solid #2a2a2a',
                      borderRadius: 8,
                    }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="deposits" stroke="#c9a84c" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="withdrawals" stroke="#e74c3c" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="income" stroke="#2ecc71" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </>
      )}
    </>
  );
}
